import type { MessageGet, MessagePost } from "@schemas/types";

import type { PushImageMessagePlacement } from "./types";

type PushMessageData = Extract<
  MessageGet | MessagePost,
  { message_type: "androidPush" | "iosPush" | "kindlePush" | "push" | "webPush" }
>;

type PushButton = {
  button_string?: string | null;
  marked_as_deleted: boolean | null;
  text?: string | null;
  uri: string | null;
};

const getUnsupportedActionButtonFields = (buttons: PushButton[] | null | undefined) =>
  (buttons ?? [])
    .filter((button) => !button.marked_as_deleted)
    .flatMap((button) => [
      { field: "action-button-text" as const, value: button.text ?? button.button_string },
      { field: "action-button-url" as const, value: button.uri },
    ]);

/** Maps a Push message to the fields supported by Content Optimization Image variants. */
export const getPushImageMessagePlacement = (message: PushMessageData): PushImageMessagePlacement | null => {
  switch (message.message_type) {
    case "push":
      return null;
    case "iosPush":
      return {
        platform: "ios",
        supportedFields: [{ field: "ios-notification-image", value: message.ios_image_url }],
        unsupportedFields: [
          { field: "title", value: message.ios_alert_hash?.title },
          { field: "message", value: message.ios_push_message },
          ...getUnsupportedActionButtonFields(message.ios_push_buttons),
        ],
      };
    case "androidPush":
      return {
        platform: "android",
        supportedFields: [{ field: "push-icon-image", value: message.android_push_icon_image_url }],
        unsupportedFields: [
          { field: "title", value: message.android_title },
          { field: "message", value: message.android_push_message },
          { field: "android-summary-text", value: message.android_summary_text },
          { field: "android-notification-image", value: message.image_url },
          ...getUnsupportedActionButtonFields(message.android_push_buttons),
        ],
      };
    case "webPush":
      return {
        platform: "web",
        supportedFields: [{ field: "push-icon-image", value: message.image_url }],
        unsupportedFields: [
          { field: "title", value: message.web_title },
          { field: "message", value: message.web_push_message },
          { field: "web-notification-image", value: message.large_image_url },
          ...getUnsupportedActionButtonFields(message.web_push_buttons),
        ],
      };
    case "kindlePush":
      return {
        platform: "kindle",
        supportedFields: [],
        unsupportedFields: [
          { field: "title", value: message.kindle_title },
          { field: "message", value: message.kindle_push_message },
          ...getUnsupportedActionButtonFields(message.kindle_push_buttons),
        ],
      };
  }
};
